import { formatters } from "@fraction/shared";
import { useCallback, useState } from "react";
import fraction from "src/api/fraction";
import config from "src/config";
import { useMutation } from "src/lib";

export function usePhoneVerify({ phone, onSuccess }: { phone?: string; onSuccess?: () => void } = {}) {
  const [phoneVerified, setPhoneVerified] = useState(!config.isProd);
  const [codeInvalidMessage, setCodeInvalidMessage] = useState<string>();

  const startVerification = useMutation({
    mutationFn: () => {
      if (!phone) {
        throw new Error("No phone");
      }
      setCodeInvalidMessage(undefined);
      setPhoneVerified(false);
      return fraction.startPhoneVerification(phone);
    },
  });

  const checkVerification = useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      if (!phone) {
        throw new Error("No phone");
      }
      return fraction.checkPhoneVerification(phone, code);
    },
    onSuccess: ({ status }) => {
      if (status === "approved") {
        setPhoneVerified(true);
        onSuccess?.();
      } else {
        setPhoneVerified(false);
        setCodeInvalidMessage("Invalid code");
      }
    },
  });

  const resetPhoneVerificationStatus = useCallback(() => {
    setPhoneVerified(!config.isProd);
    setCodeInvalidMessage(undefined);
  }, []);

  return {
    startVerification,
    checkVerification,
    isPending: startVerification.isPending || checkVerification.isPending,
    phoneAcceptable:
      !!phone &&
      formatters.phone.formatStrippedPhoneNumber(formatters.string.removeEverythingButNumbers(phone), {
        stripInternationalCode: true,
      })?.length === 10,
    phoneVerified,
    resetPhoneVerificationStatus,
    codeInvalidMessage,
  };
}
